import React from "react";

import { Text } from "@performyard/py-components";

type ClampedTextProps = Pick<React.ComponentProps<typeof Text>, "size" | "weight" | "color"> & {
	children: React.ReactNode;
	/**
	 * Width the text is clamped to, in px. CSS truncation needs a bounded, shrinkable box; the
	 * wrapper this renders provides it.
	 *
	 * Anything non-positive or undefined is treated as unbounded, so the text clamps to whatever its
	 * parent allows. Callers commonly derive this from a `useResizeObserver` width that starts at
	 * `0`/`undefined`, which would otherwise yield a zero or negative max-width on first render.
	 */
	maxWidth?: number;
	/**
	 * Whether hovering clamped text reveals the full value.
	 *
	 * `true` emits `py-text-truncate`, which subscribes the element to the app-wide
	 * `ClampedTextTooltipManager`. `false` emits plain `truncate`, which the manager ignores — use it
	 * where the original explicitly opted out of a tooltip.
	 * @default true
	 */
	withTooltip?: boolean;
	/**
	 * Extra classes for the text element itself, not the wrapper.
	 */
	dangerouslySetClassName?: string;
};

/**
 * Single-line text clamped to a pixel width, with an ellipsis and an optional hover tooltip.
 *
 * Owns the three things every clamped site needs and is easy to get wrong individually:
 * `wrap={false}` (py-components `Text` bakes `whitespace-normal` into its base variants, which
 * collides with `nowrap` at equal specificity), `min-w-0` on the wrapper it renders — which matters
 * when that wrapper is itself a flex item, since `min-width: 0` only relaxes the automatic minimum
 * size of the element it is set on — and the clamp class that decides whether a tooltip appears.
 *
 * **When to use this.** The clamp width is a runtime measurement — a `useResizeObserver` width, a
 * grid column width — which a Tailwind arbitrary value cannot express because it needs a variable.
 *
 * **When not to.** The width is a literal, or there is no width at all. Put `py-text-truncate` (or
 * `max-w-[Npx] py-text-truncate`) on the element directly.
 *
 * Renders a block wrapper `<div>`, so it is not suitable where a single inline element is required.
 */
export const ClampedText = ({
	children,
	maxWidth,
	size,
	weight,
	color,
	withTooltip = true,
	dangerouslySetClassName,
}: ClampedTextProps): React.JSX.Element => (
	<div className="min-w-0" style={{ maxWidth: maxWidth !== undefined && maxWidth > 0 ? maxWidth : undefined }}>
		<Text
			wrap={false}
			size={size}
			weight={weight}
			color={color}
			dangerouslySetClassName={`${withTooltip ? "py-text-truncate" : "truncate"} ${dangerouslySetClassName ?? ""}`.trim()}
		>
			{children}
		</Text>
	</div>
);
